import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Receipt
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectBillingService from '../../../services/projectBillingService'
import '../../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectBillingList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalBillings: 0,
    draftBillings: 0,
    approvedBillings: 0,
    invoicedBillings: 0,
    paidBillings: 0,
    overdueBillings: 0,
    totalBillingAmount: 0,
    totalPaidAmount: 0,
    totalOutstandingAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [billingsResponse, statsResponse] = await Promise.all([
        projectBillingService.getAllProjectBillings({ limit: 1000 }),
        projectBillingService.getProjectBillingStats()
      ])

      if (billingsResponse && billingsResponse.success) {
        const billings = billingsResponse.billings || []
        setData(billings.map(billing => ({
          ...billing,
          id: billing._id || billing.id
        })))
        
        // Show toast if no data
        if (billings.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project billings found. Create your first billing to get started!')
        }
      } else {
        toast.error(billingsResponse?.message || 'Failed to load project billings')
      }

      if (statsResponse && statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load project billings data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/billings/new')
  }

  const handleEdit = (billing) => {
    navigate(`/projects/billings/edit/${billing._id || billing.id}`)
  }

  const handleView = (billing) => {
    navigate(`/projects/billings/view/${billing._id || billing.id}`)
  }

  const handleDelete = (billing) => {
    setDeleteItem(billing)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectBillingService.deleteProjectBilling(deleteItem._id || deleteItem.id)
      if (response.success) {
        toast.success('Project billing deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete project billing')
      }
    } catch (error) {
      console.error('Error deleting project billing:', error)
      toast.error('Failed to delete project billing')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'billingNumber',
      label: 'Billing #',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'billingDate',
      label: 'Billing Date',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{formatDate(value)}</div>
      )
    },
    {
      key: 'projectName',
      label: 'Project',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'totalAmount',
      label: 'Total Amount',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        const currency = row.currency || 'INR'
        const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency
        return (
          <div className="font-medium text-gray-900">
            {symbol} {value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        )
      }
    },
    {
      key: 'paymentStatus',
      label: 'Payment Status',
      sortable: true,
      render: (value, row) => {
        const status = value || 'Unpaid'
        const statusColors = {
          'Unpaid': '#EF4444',
          'Partially Paid': '#F59E0B',
          'Paid': '#10B981',
          'Overdue': '#DC2626',
          'Cancelled': '#6B7280'
        }
        const statusColor = statusColors[status] || '#6B7280'
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {status}
          </span>
        )
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const status = value || 'Draft'
        const statusColors = {
          'Draft': '#6B7280',
          'Pending Approval': '#F59E0B',
          'Approved': '#10B981',
          'Invoiced': '#3B82F6',
          'Sent': '#8B5CF6',
          'Paid': '#10B981',
          'Cancelled': '#DC2626'
        }
        const statusColor = statusColors[status] || '#6B7280'
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {status}
          </span>
        )
      }
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        return <div className="font-medium text-gray-900">{formatDate(value)}</div>
      }
    }
  ]

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Transactions',
      path: '/projects?tab=transactions',
      onClick: () => navigate('/projects?tab=transactions')
    },
    {
      label: 'Project Billing',
      path: '/projects/billings',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Billing</h1>
          <p>Create, view, and manage project billings</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Billing
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Receipt size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalBillings || 0}</div>
          <div className="metric-title">Total Billings</div>
          <div className="metric-subtitle">All time</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.paidBillings || 0}</div>
          <div className="metric-title">Paid</div>
          <div className="metric-subtitle">Completed payments</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>₹{((stats.totalOutstandingAmount || 0) / 1000).toFixed(1)}K</span>
            </div>
          </div>
          <div className="metric-value">₹{((stats.totalOutstandingAmount || 0) / 100000).toFixed(1)}L</div>
          <div className="metric-title">Outstanding</div>
          <div className="metric-subtitle">Pending collection</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdueBillings || 0}</div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Need attention</div>
        </div>
      </div>

      {/* Project Billing List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search billings..."
          emptyMessage="No project billings found. Create your first billing to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Billing"
        message={`Are you sure you want to delete "${deleteItem?.billingNumber || ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProjectBillingList
